import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  importCsv,
  importSalesNav,
  searchApollo,
  importApollo,
  type ApolloLead,
} from "@/api/intake";
import { syncHubSpot, getIntegrationsStatus } from "@/api/hubspot";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { cn } from "@/cn";

type IntakeMode = "csv" | "apollo" | "salesnav" | "hubspot";

const INTAKE_MODES: { id: IntakeMode; label: string; description: string }[] = [
  {
    id: "apollo",
    label: "Apollo",
    description: "Search people and accounts by ICP filters, then import as leads.",
  },
  {
    id: "hubspot",
    label: "HubSpot",
    description: "Pull contacts, companies, and deals from your connected HubSpot CRM.",
  },
  {
    id: "salesnav",
    label: "Sales Navigator",
    description: "Paste a Sales Navigator CSV export to bulk-import leads.",
  },
  {
    id: "csv",
    label: "CSV Upload",
    description: "Generic CSV with company, deal, URL, and contact columns.",
  },
];

export function IntakePanels() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<IntakeMode>("apollo");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [csvText, setCsvText] = useState("");
  const [orgQuery, setOrgQuery] = useState("");
  const [titles, setTitles] = useState("");
  const [apolloResults, setApolloResults] = useState<ApolloLead[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { data: integrations } = useQuery({
    queryKey: ["integrations-status"],
    queryFn: getIntegrationsStatus,
    staleTime: 30_000,
    enabled: mode === "hubspot",
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["opportunities"] });
    qc.invalidateQueries({ queryKey: ["opportunities"] });
    qc.invalidateQueries({ queryKey: ["leads"] });
    qc.invalidateQueries({ queryKey: ["companies"] });
  };

  const csvMutation = useMutation({
    mutationFn: () => importCsv(csvText),
    onSuccess: (res) => {
      setMessage(`Imported ${res.created.length} lead(s), skipped ${res.skipped}.`);
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "CSV import failed");
    },
  });

  const salesNavMutation = useMutation({
    mutationFn: () => importSalesNav(csvText),
    onSuccess: (res) => {
      setMessage(`Imported ${res.created.length} lead(s), skipped ${res.skipped}.`);
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "SalesNav import failed");
    },
  });

  const apolloSearchMutation = useMutation({
    mutationFn: () =>
      searchApollo({
        q_organization_name: orgQuery || undefined,
        person_titles: titles
          ? titles.split(",").map((s) => s.trim()).filter(Boolean)
          : undefined,
      }),
    onSuccess: (res) => {
      setApolloResults(res.leads);
      setSelected(new Set(res.leads.map((_, i) => i)));
      setMessage(`Found ${res.total} result(s).`);
      setError(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Apollo search failed");
    },
  });

  const apolloImportMutation = useMutation({
    mutationFn: () => {
      const leads = apolloResults.filter((_, i) => selected.has(i));
      return importApollo(leads);
    },
    onSuccess: (res) => {
      setMessage(`Imported ${res.created.length} lead(s), skipped ${res.skipped}.`);
      setError(null);
      invalidate();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "Apollo import failed");
    },
  });

  const hubspotSyncMutation = useMutation({
    mutationFn: syncHubSpot,
    onSuccess: (res) => {
      setMessage(
        `HubSpot sync complete — ` +
          `${res.companies.created + res.companies.updated} companies, ` +
          `${res.contacts.created + res.contacts.updated} contacts, ` +
          `${res.deals.created + res.deals.updated} deals.`,
      );
      setError(null);
      invalidate();
      qc.invalidateQueries({ queryKey: ["integrations-status"] });
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : "HubSpot sync failed");
    },
  });

  function toggleSelected(index: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }

  return (
    <div>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {INTAKE_MODES.map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={cn(
              "rounded-lg border p-4 text-left transition",
              mode === m.id
                ? "border-brand bg-brand/5"
                : "border-border bg-card hover:border-muted",
            )}
          >
            <h3 className="font-medium text-foreground">{m.label}</h3>
            <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>
          </button>
        ))}
      </div>

      {mode === "apollo" && (
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-foreground">Organization</label>
              <input
                value={orgQuery}
                onChange={(e) => setOrgQuery(e.target.value)}
                placeholder="e.g. Acme Corp"
                className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground">Titles (comma-separated)</label>
              <input
                value={titles}
                onChange={(e) => setTitles(e.target.value)}
                placeholder="VP Sales, Head of Revenue"
                className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => apolloSearchMutation.mutate()}
              disabled={apolloSearchMutation.isPending}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
            >
              {apolloSearchMutation.isPending ? "Searching…" : "Search Apollo"}
            </button>
            {apolloResults.length > 0 && (
              <button
                type="button"
                onClick={() => apolloImportMutation.mutate()}
                disabled={apolloImportMutation.isPending || selected.size === 0}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                Import selected ({selected.size})
              </button>
            )}
          </div>
          {apolloResults.length > 0 && (
            <ul className="mt-4 max-h-64 space-y-2 overflow-y-auto">
              {apolloResults.map((lead, i) => (
                <li key={`${lead.company}-${i}`} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.has(i)}
                    onChange={() => toggleSelected(i)}
                    className="mt-1"
                  />
                  <span>
                    <span className="font-medium">{lead.company}</span>
                    <span className="text-muted-foreground"> — {lead.dealName}</span>
                    {lead.leadName && (
                      <span className="block text-xs text-muted-foreground">
                        {lead.leadName}
                        {lead.contactTitle ? ` · ${lead.contactTitle}` : ""}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {mode === "hubspot" && (
        <div className="rounded-lg border border-border bg-card p-5 space-y-4">
          {integrations?.hubspot.connected ? (
            <>
              <p className="text-sm text-muted-foreground">
                Connected
                {integrations.hubspot.portalId
                  ? ` (portal ${integrations.hubspot.portalId})`
                  : ""}
                . Pull companies, contacts, and deals into Zero. Stage changes
                push back automatically.
              </p>
              {integrations.hubspot.lastSyncAt && (
                <p className="text-xs text-muted-foreground">
                  Last sync:{" "}
                  {new Date(integrations.hubspot.lastSyncAt).toLocaleString()}
                </p>
              )}
              <button
                type="button"
                onClick={() => hubspotSyncMutation.mutate()}
                disabled={hubspotSyncMutation.isPending}
                className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
              >
                {hubspotSyncMutation.isPending ? "Syncing…" : "Sync from HubSpot"}
              </button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Connect HubSpot in{" "}
              <Link to="/settings/integrations" className="text-brand underline">
                Settings → Integrations
              </Link>{" "}
              by pasting your Private App access token.
            </p>
          )}
        </div>
      )}

      {(mode === "csv" || mode === "salesnav") && (
        <div className="rounded-lg border border-border bg-card p-5">
          <label className="text-xs font-medium text-foreground">
            {mode === "salesnav" ? "Sales Navigator CSV export" : "CSV content"}
          </label>
          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            rows={8}
            placeholder={
              mode === "salesnav"
                ? "Paste your Sales Navigator export (Company, First Name, Title, …)"
                : "company,dealName,url,notes\nAcme Corp,Expansion deal,https://acme.com,…"
            }
            className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs"
          />
          <button
            type="button"
            onClick={() =>
              mode === "salesnav" ? salesNavMutation.mutate() : csvMutation.mutate()
            }
            disabled={
              !csvText.trim() ||
              (mode === "salesnav" ? salesNavMutation : csvMutation).isPending
            }
            className="mt-4 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            Import
          </button>
        </div>
      )}

      {message && <p className="mt-4 text-sm text-emerald-500">{message}</p>}
      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
    </div>
  );
}
